import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileTextIcon,
  CopyIcon,
  CheckIcon,
  XIcon,
  EyeIcon,
  SearchIcon,
  MailIcon,
  PhoneIcon,
  LinkIcon,
  UserIcon,
  ShuffleIcon,
  ClipboardListIcon,
  FolderIcon,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { tenantApi, FormType, SubmissionStatus, FormSubmission } from '../lib/tenant-api';
import { PageLoader, Pagination } from '../components/common';
import { StatCard } from '../components/StatCard';

const FORM_TYPE_CONFIG: Record<
  FormType,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  LINK: { label: 'Link', icon: LinkIcon },
  FORM_EMAIL: { label: 'Email form', icon: MailIcon },
  FORM_PHONE: { label: 'Phone form', icon: PhoneIcon },
  FORM_NAME: { label: 'Name form', icon: UserIcon },
  FORM_FULL_DETAILS: { label: 'Full details form', icon: ClipboardListIcon },
  DYNAMIC_FORM: { label: 'Dynamic form', icon: ShuffleIcon },
  SINGLE_FORM: { label: 'Single form', icon: FileTextIcon },
  OTHER: { label: 'Other', icon: FileTextIcon },
};

function formatFormTypeLabel(formType: FormType): string {
  return FORM_TYPE_CONFIG[formType]?.label ?? formType.replace(/_/g, ' ').toLowerCase();
}

function FormTypeBadge({ formType }: { formType: FormType }) {
  const config = FORM_TYPE_CONFIG[formType] ?? FORM_TYPE_CONFIG.OTHER;
  const Icon = config.icon;

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-200">
      <Icon className="h-3.5 w-3.5 text-gray-500" />
      {config.label}
    </span>
  );
}

function StatusBadge({ status }: { status: SubmissionStatus }) {
  const classes = {
    RECEIVED: 'bg-indigo-100 text-indigo-800',
    PROCESSED: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800',
  }[status] ?? 'bg-gray-100 text-gray-800';

  return (
    <span className={clsx('inline-flex px-2.5 py-1 text-xs font-semibold rounded-full', classes)}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

function truncateId(value: string, length = 8): string {
  if (value.length <= length) return value;
  return `${value.substring(0, length)}...`;
}

export default function Forms() {
  const [formTypeFilter, setFormTypeFilter] = useState<FormType | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'ALL'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<FormSubmission | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const limit = 20;
  const offset = (currentPage - 1) * limit;
  const showClearFilters = formTypeFilter !== 'ALL' || statusFilter !== 'ALL' || searchTerm !== '';

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length === 0 || searchTerm.length > 2) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [formTypeFilter, statusFilter]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['form-submission-stats'],
    queryFn: tenantApi.getFormSubmissionStats,
  });

  const { data: formsResponse, isLoading, error } = useQuery({
    queryKey: ['form-submissions', formTypeFilter, statusFilter, debouncedSearchTerm, currentPage],
    queryFn: () =>
      tenantApi.getTenantForms(
        formTypeFilter === 'ALL' ? undefined : formTypeFilter,
        statusFilter === 'ALL' ? undefined : statusFilter,
        limit,
        offset,
        debouncedSearchTerm || undefined
      ),
  });

  const forms = formsResponse?.items || [];
  const totalPages = formsResponse?.total_pages || 0;
  const total = formsResponse?.total || 0;
  const isSearchInProgress = searchTerm !== debouncedSearchTerm && searchTerm.length > 0;

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    if (key.startsWith('row-')) {
      setCopiedId(text);
      setTimeout(() => setCopiedId(null), 2000);
      return;
    }
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClearFilters = () => {
    setFormTypeFilter('ALL');
    setStatusFilter('ALL');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatDateTime = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatFieldLabel = (key: string) =>
    key
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  if (isLoading && statsLoading) {
    return <PageLoader />;
  }

  return (
    <div>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Forms</h1>
          <p className="mt-2 text-sm text-gray-600">Manage and track form submissions</p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Forms" value={stats?.total ?? 0} icon={FileTextIcon} skipTitleCase />
          <StatCard title="Received" value={stats?.received ?? 0} icon={FolderIcon} skipTitleCase />
          <StatCard title="Processed" value={stats?.processed ?? 0} icon={CheckCircle} skipTitleCase />
          <StatCard title="Failed" value={stats?.failed ?? 0} icon={XCircle} skipTitleCase />
        </div>

        <div className="bg-white shadow border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search form"
                className="w-full pl-10 pr-10 py-3 text-sm bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {isSearchInProgress && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              )}
            </div>

            <select
              value={formTypeFilter}
              onChange={(e) => setFormTypeFilter(e.target.value as FormType | 'ALL')}
              className="px-4 py-3 w-full lg:w-44 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ALL">All Types</option>
              <option value="SINGLE_FORM">Single form</option>
              <option value="FORM_EMAIL">Email form</option>
              <option value="FORM_PHONE">Phone form</option>
              <option value="LINK">Link</option>
              <option value="FORM_NAME">Name form</option>
              <option value="DYNAMIC_FORM">Dynamic form</option>
              <option value="FORM_FULL_DETAILS">Full details form</option>
              <option value="OTHER">Other</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SubmissionStatus | 'ALL')}
              className="px-4 py-3 w-full lg:w-44 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ALL">All Status</option>
              <option value="RECEIVED">Received</option>
              <option value="PROCESSED">Processed</option>
              <option value="FAILED">Failed</option>
            </select>

            {showClearFilters && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-3 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="bg-white shadow border border-gray-200">
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Form Submissions</h2>

            {isLoading && (
              <div className="text-center py-12">
                <div className="text-lg text-gray-600">Loading form submissions...</div>
              </div>
            )}

            {!isLoading && !!error && (
              <div className="text-center py-12">
                <div className="text-lg text-red-600">Error loading form submissions</div>
                <p className="mt-2 text-sm text-gray-500">
                  Please try again later or contact support if the problem persists.
                </p>
              </div>
            )}

            {!isLoading && !error && forms.length === 0 && (
              <div className="text-center py-8">
                <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No form submissions found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {showClearFilters
                    ? 'Try adjusting your filters.'
                    : 'Form submissions will appear here when users submit forms through your AI assistant.'}
                </p>
              </div>
            )}

            {!isLoading && !error && forms.length > 0 && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Form ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Form type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Session ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {forms.map((form) => (
                        <tr key={form.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-900 font-mono">{truncateId(form.form_id)}</span>
                              <button
                                onClick={() => handleCopy(form.form_id, `row-${form.form_id}`)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Copy form ID"
                              >
                                {copiedId === form.form_id ? (
                                  <CheckIcon className="h-4 w-4 text-green-600" />
                                ) : (
                                  <CopyIcon className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <FormTypeBadge formType={form.form_type} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {form.session_id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-900 font-mono">
                                  {truncateId(form.session_id)}
                                </span>
                                <button
                                  onClick={() => handleCopy(form.session_id!, `row-${form.session_id}`)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Copy session ID"
                                >
                                  {copiedId === form.session_id ? (
                                    <CheckIcon className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <CopyIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={form.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(form.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => setSelectedForm(form)}
                              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900"
                              title="View form details"
                            >
                              <EyeIcon className="h-4 w-4" />
                              View details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {total > limit && (
                  <Pagination
                    className="mt-6"
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    rangeStart={forms.length > 0 ? (formsResponse?.offset || 0) + 1 : 0}
                    rangeEnd={(formsResponse?.offset || 0) + forms.length}
                    totalItems={total}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {selectedForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 px-2 sm:px-4">
          <div className="relative top-4 sm:top-16 mx-auto border w-full max-w-2xl shadow-lg bg-white max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Form Submission Details</h3>
              <button
                onClick={() => setSelectedForm(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <DetailField
                label="Form ID"
                value={selectedForm.form_id}
                copyKey="form_id"
                copiedField={copiedField}
                onCopy={handleCopy}
                mono
              />
              <DetailField
                label="Form type"
                value={formatFormTypeLabel(selectedForm.form_type)}
              />
              <DetailField
                label="Session ID"
                value={selectedForm.session_id || 'Not provided'}
                copyKey="session_id"
                copiedField={copiedField}
                onCopy={handleCopy}
                mono
                copyable={!!selectedForm.session_id}
              />
              <DetailField
                label="Client WS ID"
                value={selectedForm.client_ws_id || 'Not provided'}
                copyKey="client_ws_id"
                copiedField={copiedField}
                onCopy={handleCopy}
                mono
                copyable={!!selectedForm.client_ws_id}
              />
              <DetailField label="Status" value={selectedForm.status.charAt(0) + selectedForm.status.slice(1).toLowerCase()} />
              <DetailField label="Created" value={formatDateTime(selectedForm.created_at)} />

              <div className="pt-4 border-t border-gray-200">
                <div className="bg-gray-50 rounded-md p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-semibold text-gray-900">Form Data</h4>
                    <button
                      onClick={() =>
                        handleCopy(JSON.stringify(selectedForm.form_data, null, 2), 'form_data')
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-200 hover:bg-gray-300 rounded"
                    >
                      {copiedField === 'form_data' ? (
                        <>
                          <CheckIcon className="h-4 w-4 text-green-600" />
                          <span className="text-green-600">Copied!</span>
                        </>
                      ) : (
                        <>
                          <CopyIcon className="h-4 w-4" />
                          Copy as JSON
                        </>
                      )}
                    </button>
                  </div>

                  {selectedForm.form_data && typeof selectedForm.form_data === 'object' ? (
                    <div className="space-y-3">
                      {Object.entries(selectedForm.form_data).map(([key, value]) => (
                        <div
                          key={key}
                          className="flex w-full items-start justify-between gap-4 text-sm"
                        >
                          <span className="font-medium text-gray-600 shrink-0">
                            {formatFieldLabel(key)}:
                          </span>
                          <span className="font-semibold text-gray-900 text-right break-all">
                            {typeof value === 'object' && value !== null
                              ? JSON.stringify(value)
                              : String(value || 'N/A')}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No form data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailField({
  label,
  value,
  copyKey,
  copiedField,
  onCopy,
  mono = false,
  copyable = true,
}: {
  label: string;
  value: string;
  copyKey?: string;
  copiedField?: string | null;
  onCopy?: (text: string, key: string) => void;
  mono?: boolean;
  copyable?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          readOnly
          value={value}
          className={clsx(
            'block w-full border border-gray-300 px-3 py-2.5 bg-white text-sm text-gray-900 focus:outline-none',
            mono && 'font-mono',
            copyable && 'pr-10'
          )}
        />
        {copyable && copyKey && onCopy && value !== 'Not provided' && (
          <button
            onClick={() => onCopy(value, copyKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            title={`Copy ${label}`}
          >
            {copiedField === copyKey ? (
              <CheckIcon className="h-4 w-4 text-green-600" />
            ) : (
              <CopyIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
